import { create } from 'zustand';
import type { Notification } from '@/types/notification';

interface NotificationStore {
  notifications: Notification[];
  unreadCount: number;
  hasMore: boolean;
  isConnected: boolean;
  paginationCursor: string | undefined;
  snapshotVersion: number;

  init: (
    notifications: Notification[],
    unreadCount: number,
    previousNotifications?: Notification[]
  ) => void;
  pushNotification: (notif: Notification) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  markActionTaken: (id: string) => void;
  closeInvitation: (id: string, outcome: 'cancelled' | 'expired') => void;
  setConnected: (v: boolean) => void;
  appendNotifications: (items: Notification[]) => void;
  setHasMore: (v: boolean) => void;
  reset: () => void;
}

function mergeNotification(incoming: Notification, existing?: Notification): Notification {
  if (!existing) return incoming;
  return {
    ...incoming,
    isRead: incoming.isRead || existing.isRead,
    actionTaken: incoming.actionTaken || existing.actionTaken,
    readAt: incoming.readAt ?? existing.readAt,
  };
}

function sortNotifications(items: Notification[]) {
  return [...items].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

function mergeNotificationLists(
  incoming: Notification[],
  existing: Notification[],
  options: { appendOnly?: boolean; previousIds?: Set<string> } = {}
) {
  const byId = new Map<string, Notification>();
  for (const item of existing) {
    byId.set(item.notificationId, item);
  }

  const mergedIncoming = incoming.map((item) =>
    mergeNotification(item, byId.get(item.notificationId))
  );
  const incomingIds = new Set(incoming.map((item) => item.notificationId));

  if (options.appendOnly) {
    const incomingById = new Map(mergedIncoming.map((item) => [item.notificationId, item]));
    return [
      ...existing.map((item) => incomingById.get(item.notificationId) ?? item),
      ...mergedIncoming.filter((item) => !byId.has(item.notificationId)),
    ];
  }

  const preservedExisting = existing.filter(
    (item) =>
      !incomingIds.has(item.notificationId) && !options.previousIds?.has(item.notificationId)
  );
  return sortNotifications([...mergedIncoming, ...preservedExisting]);
}

function visibleUnreadCount(items: Notification[]) {
  return items.filter((item) => !item.isRead).length;
}

export const useNotificationStore = create<NotificationStore>((set) => ({
  notifications: [],
  unreadCount: 0,
  hasMore: true,
  isConnected: false,
  paginationCursor: undefined,
  snapshotVersion: 0,

  init: (notifications, unreadCount, previousNotifications) =>
    set((s) => {
      const existingById = new Map(s.notifications.map((item) => [item.notificationId, item]));
      const staleReadRegressions = notifications.filter((item) => {
        const existing = existingById.get(item.notificationId);
        return existing?.isRead && !item.isRead;
      }).length;
      // 首屏快照淘汰请求前的旧分页，仅保留请求期间新到达的通知。
      const merged = mergeNotificationLists(notifications, s.notifications, {
        previousIds: previousNotifications
          ? new Set(previousNotifications.map((item) => item.notificationId))
          : undefined,
      });
      const adjustedUnreadCount = Math.max(0, unreadCount - staleReadRegressions);
      return {
        notifications: merged,
        unreadCount: Math.max(adjustedUnreadCount, visibleUnreadCount(merged)),
        // 游标取实际服务端页尾，不能取合并进来的实时通知。
        paginationCursor: notifications.at(-1)?.createdAt,
        snapshotVersion: s.snapshotVersion + 1,
      };
    }),

  pushNotification: (notif) =>
    set((s) => {
      const existing = s.notifications.find((item) => item.notificationId === notif.notificationId);
      if (existing) {
        return {
          notifications: sortNotifications(
            s.notifications.map((item) =>
              item.notificationId === notif.notificationId ? mergeNotification(notif, item) : item
            )
          ),
          unreadCount: s.unreadCount,
        };
      }
      return {
        notifications: [notif, ...s.notifications],
        unreadCount: s.unreadCount + (notif.isRead ? 0 : 1),
      };
    }),

  markRead: (id) =>
    set((s) => ({
      notifications: s.notifications.map((n) =>
        n.notificationId === id ? { ...n, isRead: true } : n
      ),
      unreadCount: Math.max(0, s.unreadCount - 1),
    })),

  markAllRead: () =>
    set((s) => ({
      notifications: s.notifications.map((n) => ({ ...n, isRead: true })),
      unreadCount: 0,
    })),

  markActionTaken: (id) =>
    set((s) => ({
      notifications: s.notifications.map((n) =>
        n.notificationId === id ? { ...n, actionTaken: true, isRead: true } : n
      ),
      unreadCount: s.notifications.find((n) => n.notificationId === id && !n.isRead)
        ? Math.max(0, s.unreadCount - 1)
        : s.unreadCount,
    })),

  closeInvitation: (id, outcome) =>
    set((s) => ({
      notifications: s.notifications.map((n) =>
        n.notificationId === id
          ? {
              ...n,
              actionTaken: true,
              isRead: true,
              metadata: { ...n.metadata, invitationOutcome: outcome },
            }
          : n
      ),
      unreadCount: s.notifications.find((n) => n.notificationId === id && !n.isRead)
        ? Math.max(0, s.unreadCount - 1)
        : s.unreadCount,
    })),

  setConnected: (v) => set({ isConnected: v }),

  appendNotifications: (items) =>
    set((s) => ({
      notifications: mergeNotificationLists(items, s.notifications, { appendOnly: true }),
      paginationCursor: items.at(-1)?.createdAt ?? s.paginationCursor,
    })),

  setHasMore: (v) => set({ hasMore: v }),

  reset: () =>
    set((s) => ({
      notifications: [],
      unreadCount: 0,
      hasMore: true,
      isConnected: false,
      paginationCursor: undefined,
      snapshotVersion: s.snapshotVersion + 1,
    })),
}));
